import {
  MessageCircle,
  AlertCircle,
  CheckCircle2,
  Clock,
  PauseCircle,
  XCircle,
  Trash2,
  ArrowDownToLine,
  ArrowUpFromLine,
  LogOut,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  useWhatsappSession,
  useWhatsappChats,
  useRemoveWhatsappChat,
  useDisconnectWhatsapp,
} from "@/lib/hooks/useWhatsapp";
import type { WhatsappMonitoredChat, WhatsappSession } from "@/lib/types";

// ---------------------------------------------------------------------------
// WhatsAppPage - status do canal WhatsApp (Evolution API) e chats monitorados
// ---------------------------------------------------------------------------

export function WhatsAppPage(): JSX.Element {
  const { data: session, isLoading: loadingSession, error: sessionError } = useWhatsappSession();
  const { data: chats = [], isLoading: loadingChats, error: chatsError } = useWhatsappChats();

  const origens = chats.filter((c) => c.role === "origem");
  const destinos = chats.filter((c) => c.role === "destino");
  const connected = session?.status === "active";

  return (
    <div className="max-w-4xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">WhatsApp</h1>
          <p className="text-sm text-muted-foreground">
            Canal WhatsApp do seu número, monitorando grupos e repostando ofertas convertidas.
          </p>
        </div>
        <SessionPill session={session} loading={loadingSession} />
      </header>

      {(sessionError || chatsError) && (
        <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle size={16} className="shrink-0" />
          <span>
            Erro ao carregar o canal: {(sessionError ?? chatsError)?.message}
          </span>
        </div>
      )}

      <ConnectionCard session={session} loading={loadingSession} />

      {connected && (
        <div className="grid gap-6 md:grid-cols-2">
          <ChatColumn
            title="Grupos de origem"
            hint="Grupos monitorados de onde as ofertas são lidas."
            icon={ArrowUpFromLine}
            chats={origens}
            loading={loadingChats}
            emptyLabel="Nenhum grupo de origem registrado."
          />
          <ChatColumn
            title="Destinos"
            hint="Chats que recebem a oferta convertida."
            icon={ArrowDownToLine}
            chats={destinos}
            loading={loadingChats}
            emptyLabel="Nenhum destino registrado."
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SessionPill - indicador compacto de status no cabecalho
// ---------------------------------------------------------------------------

function SessionPill({
  session,
  loading,
}: {
  session: WhatsappSession | null | undefined;
  loading: boolean;
}): JSX.Element | null {
  if (loading) return null;

  const status = session?.status ?? "none";
  const cfg = SESSION_STATUS[status] ?? SESSION_STATUS_NONE;

  return (
    <div
      className={cn(
        "shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium",
        cfg.pillClass,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", cfg.dotClass)} />
      {cfg.label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConnectionCard - status detalhado + guia de conexao
// ---------------------------------------------------------------------------

function ConnectionCard({
  session,
  loading,
}: {
  session: WhatsappSession | null | undefined;
  loading: boolean;
}): JSX.Element {
  const disconnect = useDisconnectWhatsapp();

  if (loading) {
    return (
      <div
        className="h-32 w-full animate-pulse rounded-xl border border-border bg-[hsl(var(--surface-raised)/0.4)]"
        aria-busy="true"
        aria-label="Carregando status do WhatsApp"
      />
    );
  }

  if (session?.status === "active") {
    return (
      <div className="flex items-center gap-4 rounded-xl border border-primary/20 bg-primary/5 px-5 py-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle2 size={22} className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-foreground">Número conectado</p>
          <p className="mt-0.5 font-mono text-sm text-muted-foreground">{session.phone}</p>
          {session.last_connected_at && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Última conexão {formatDate(session.last_connected_at)}
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            if (window.confirm("Tem certeza que deseja desconectar o WhatsApp?")) {
              void disconnect.mutateAsync();
            }
          }}
          disabled={disconnect.isPending}
          className="shrink-0 gap-1.5 text-destructive hover:text-destructive"
        >
          <LogOut size={14} />
          {disconnect.isPending ? "Desconectando..." : "Desconectar"}
        </Button>
      </div>
    );
  }

  const revoked = session?.status === "revoked";

  return (
    <div className="space-y-5 rounded-xl border border-dashed border-border bg-[hsl(var(--surface)/0.5)] p-8 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent/10">
        <MessageCircle size={24} className="text-accent" />
      </div>

      <div className="mx-auto max-w-md space-y-2">
        <p className="font-semibold text-foreground">
          {revoked ? "Número desconectado" : "Conecte seu número de WhatsApp"}
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          A conexão usa um código de pareamento de 8 dígitos ou QR Code. Recomendamos um chip
          dedicado para o canal, separado do seu número pessoal.
        </p>
      </div>

      <div className="mx-auto w-full max-w-sm space-y-2.5 rounded-lg bg-[hsl(var(--surface-raised)/0.6)] px-5 py-4 text-left text-sm text-muted-foreground">
        <p className="mb-0.5 text-xs font-semibold uppercase tracking-widest text-foreground">
          Como conectar
        </p>
        <StepItem n={1}>Inicie o pareamento e informe o número (com DDI)</StepItem>
        <StepItem n={2}>Abra o WhatsApp e vá em Aparelhos conectados</StepItem>
        <StepItem n={3}>Toque em Conectar com número de telefone</StepItem>
        <StepItem n={4}>Digite o código de 8 dígitos exibido no pareamento</StepItem>
      </div>

      <Button asChild variant="gold">
        <Link to="/app/settings/integracao">
          <MessageCircle size={15} />
          Conectar em Integrações
        </Link>
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChatColumn - lista de chats por papel (origem/destino)
// ---------------------------------------------------------------------------

interface ChatColumnProps {
  title: string;
  hint: string;
  icon: typeof MessageCircle;
  chats: WhatsappMonitoredChat[];
  loading: boolean;
  emptyLabel: string;
}

function ChatColumn({ title, hint, icon: Icon, chats, loading, emptyLabel }: ChatColumnProps): JSX.Element {
  const removeChat = useRemoveWhatsappChat();

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon size={16} className="text-muted-foreground" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>

      {loading && (
        <div className="h-16 animate-pulse rounded-xl border border-border bg-[hsl(var(--surface-raised)/0.35)]" />
      )}

      {!loading && chats.length === 0 && (
        <div className="rounded-xl border border-dashed border-border bg-[hsl(var(--surface)/0.5)] px-4 py-6 text-center text-sm text-muted-foreground">
          {emptyLabel}
        </div>
      )}

      {!loading &&
        chats.map((chat) => (
          <div
            key={chat.id}
            className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3"
          >
            <ChatStatusIcon status={chat.status} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">
                {chat.title ?? "Chat sem nome"}
              </p>
              <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">{chat.jid}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => void removeChat.mutateAsync(chat.id)}
              disabled={removeChat.isPending}
              aria-label={`Remover ${chat.title ?? chat.jid}`}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface SessionStatusCfg {
  label: string;
  pillClass: string;
  dotClass: string;
}

const SESSION_STATUS_NONE: SessionStatusCfg = {
  label: "Não conectado",
  pillClass: "border-border bg-[hsl(var(--surface-raised))] text-muted-foreground",
  dotClass: "bg-muted-foreground",
};

const SESSION_STATUS: Record<string, SessionStatusCfg> = {
  active: {
    label: "Conectado",
    pillClass: "border-primary/20 bg-primary/10 text-primary",
    dotClass: "bg-primary animate-pulse",
  },
  pending: {
    label: "Pareando",
    pillClass: "border-accent/20 bg-accent/10 text-accent",
    dotClass: "bg-accent",
  },
  revoked: {
    label: "Desconectado",
    pillClass: "border-border bg-[hsl(var(--surface-raised))] text-muted-foreground",
    dotClass: "bg-muted-foreground",
  },
  none: SESSION_STATUS_NONE,
};

function StepItem({ n, children }: { n: number; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/15 font-mono text-[10px] font-bold text-accent">
        {n}
      </span>
      <span className="text-sm leading-snug">{children}</span>
    </div>
  );
}

function ChatStatusIcon({ status }: { status: WhatsappMonitoredChat["status"] }): JSX.Element {
  if (status === "active") {
    return <CheckCircle2 size={18} className="shrink-0 text-primary" aria-hidden="true" />;
  }
  if (status === "error_not_member") {
    return <XCircle size={18} className="shrink-0 text-destructive" aria-hidden="true" />;
  }
  if (status === "paused") {
    return <PauseCircle size={18} className="shrink-0 text-muted-foreground" aria-hidden="true" />;
  }
  return <Clock size={18} className="shrink-0 text-accent" aria-hidden="true" />;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
